import { Box, TableCell } from '@mui/material'
import { action } from 'mobx'
import { useMemo } from 'react'
import { LogEntry, LogLevel } from '../../../api/DTO'
import { LogTableFilters, LogTableFiltersReturn, TableColumn } from '../../../components/tables/DTO'
import { FilteredTable } from '../../../components/tables/FilteredTable'
import { FilterControl } from '../../../components/tables/filters/DTO'
import { toastService } from '../../../services/notifications/ToastService'
import { LogLevelChip } from './LogLevelChip'

type LogTableProps = {
	getEntries?: (params: LogTableFiltersReturn) => Promise<LogEntry[]>
	getEntriesCount?: (params: LogTableFiltersReturn) => Promise<number>
	entries?: LogEntry[]
	tableWrap?: Record<string, unknown>
	editorHeight?: string
	editorWidth?: string
	maxTableHeight?: string
}

export const LogTable = (props: LogTableProps) => {
	const controlled = !!props.getEntries && !!props.getEntriesCount

	const getEntries = controlled
		? (filters: LogTableFilters, callback: (data: LogEntry[]) => void) =>
			props.getEntries!({
				logLevel: filters.logLevel,
				eventDateFrom: filters.eventDate?.start?.toJSON(),
				eventDateTo: filters.eventDate?.end?.toJSON(),
				pageNumber: filters.pageNumber,
				pageSize: filters.pageSize,
				sortBy: filters.sortBy as number ?? 0,
			}).then((data) => callback(data))
				.catch((e) => {
					console.error(e)
					toastService.displayToast({
						message: 'Error loading table entries',
						area: 'global',
						severity: 'error',
					})
				})
		: undefined

	const getEntriesCount = controlled
		? (filters: Record<string, unknown>, callback: (count: number) => void) => {
			const logFilters = filters as LogTableFilters
			return props.getEntriesCount!({
				logLevel: logFilters.logLevel,
				eventDateFrom: logFilters.eventDate?.start?.toJSON(),
				eventDateTo: logFilters.eventDate?.end?.toJSON()
			})
				.then((data) => callback(data))
				.catch((e) => {
					console.error(e)
					toastService.displayToast({
						message: 'Error loading table entries count',
						area: 'global',
						severity: 'error',
					})
				})
		}
		: undefined

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'date range',
			name: 'eventDate',
			label: 'Date'
		},
		{
			type: 'log level',
			name: 'logLevel',
			label: 'Log Level'
		},
		{
			type: 'sort by age',
			name: 'sortBy',
			isDisplaySetting: true
		},
		{
			type: "table display",
			name: "display",
			isDisplaySetting: true
		}
	], [])

	const justJson: FilterControl[] = useMemo(() => [
		{
			type: "table display",
			name: "display",
			isDisplaySetting: true
		}
	], [])

	const columns: TableColumn<LogEntry>[] = useMemo(() => [
		{
			name: "eventId",
			label: "Event"
		},
		{
			name: "logLevel",
			label: "Level",
			renderContent: (v) => <LogLevelChip logLevel={v.logLevel as LogLevel} />
		},
		{
			name: "eventDate",
			label: "Time",
			renderContent: (v) => new Date(v.eventDate).toISOString()
		},
		{
			name: "message",
			label: "Message",
			renderCell: (v) =>
				<Box
					key={3}
					component={TableCell}
					sx={{ wordWrap: 'break-word', whiteSpace: 'none' }}
				>
					{v.eventData !== undefined
						? getFormattedMessage(
							v.message as string,
							v.eventData as unknown[],
						)
						: v.message as string}
				</Box>
		}
	], [])

	return (
		<FilteredTable
			columns={columns}
			filterControls={controlled ? filterControls : justJson}
			getEntries={getEntries}
			getEntriesCount={getEntriesCount}
			allEntries={props.entries}
			maxTableHeight={props.maxTableHeight}
			editorHeight={props.editorHeight}
			editorWidth={props.editorWidth}
		/>
	)
}

const getFormattedMessage = action(
	(message: string, eventData: unknown[]): string =>
		message.replace(/{[0-9]+}/g, (match: string) => {
			const strIndex = match.substring(1, match.length - 1)
			const index = parseInt(strIndex)
			return eventData[index] !== undefined
				? (eventData[index] as string)
				: match
		}),
)